import { useTranslation } from 'react-i18next';
import { usePWAUpdate } from '@/contexts/PWAUpdateContext';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Download, RefreshCw, Sparkles, FileText, PartyPopper, X, Flame } from 'lucide-react';
import { Link } from 'react-router-dom';
import { logos } from '@/config/seasonalEvents';
import { useHolidayTheme } from '@/hooks/useHolidayTheme';
import { isStandalonePWA } from '@/lib/isStandalonePWA';

/**
 * Unified PWA Update Manager
 * Consolidates PWAUpdatePrompt, PWAUpdatingOverlay, and PWAUpdateNotice into one component
 */
export const PWAUpdateManager = () => {
  const { t } = useTranslation();
  const { kind } = useHolidayTheme();
  const seasonalLogo =
    kind === "halloween"
      ? logos.halloween
      : kind === "valentine"
        ? logos.valentine
        : kind === "new_year"
          ? logos.newYear
          : kind === "christmas"
            ? logos.christmas
            : logos.regular;
  const { 
    needsRefresh, 
    isUpdating, 
    wasJustUpdated,
    updateServiceWorker, 
    dismissUpdate,
    dismissUpdateNotice,
    appVersion 
  } = usePWAUpdate();

  // Update prompts/overlays are PWA-only — the website always serves the latest build.
  if (!isStandalonePWA()) return null;

  // Full-screen updating overlay
  if (isUpdating) {
    return (
      <div className="fixed inset-0 z-[9999] bg-background flex flex-col items-center justify-center">
        <div className="flex flex-col items-center gap-6 text-center px-4">
          {/* Logo with animation */}
          <div className="relative">
            <img 
              src={seasonalLogo} 
              alt="ChaosKitchen" 
              className="w-24 h-24 animate-pulse"
            />
            <div className="absolute -inset-4 rounded-full bg-primary/20 animate-ping" />
          </div>

          {/* Loading spinner */}
          <div className="relative">
            <RefreshCw className="w-12 h-12 text-primary animate-spin" />
          </div>

          {/* Text */}
          <div className="space-y-2">
            <h2 className="text-2xl font-heading font-bold text-foreground">
              {t('pwa.updatingTitle', { defaultValue: 'Updating ChaosKitchen' })}
            </h2>
            <p className="text-muted-foreground max-w-sm">
              {t('pwa.updatingMessage', { defaultValue: 'Please wait while we serve you the latest and greatest...' })}
            </p>
          </div>

          {/* Fun cooking animation */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-4">
            <Flame className="w-4 h-4 text-primary animate-bounce" />
            <span>{t('pwa.cookingNewFeatures', { defaultValue: 'Cooking up new features...' })}</span>
          </div>
        </div>
      </div>
    );
  }

  // Update complete notice dialog
  if (wasJustUpdated) {
    return (
      <Dialog open={wasJustUpdated} onOpenChange={(open) => !open && dismissUpdateNotice()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PartyPopper className="w-5 h-5 text-primary" />
              {t('pwa.updateComplete', { defaultValue: 'Update Complete!' })}
            </DialogTitle>
            <DialogDescription>
              {t('pwa.updateCompleteMessage', { defaultValue: 'ChaosKitchen has been updated to the latest version. Enjoy the new features!' })}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="flex items-center justify-center gap-2 p-4 rounded-lg bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20">
              <span className="text-lg font-semibold text-foreground">
                v{appVersion}
              </span>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={dismissUpdateNotice}
              className="flex-1"
            >
              <X className="w-4 h-4 mr-2" />
              {t('common.close', { defaultValue: 'Close' })}
            </Button>
            <Button
              asChild
              className="flex-1"
            >
              <Link to="/changelog" onClick={dismissUpdateNotice}>
                <FileText className="w-4 h-4 mr-2" />
                {t('pwa.viewChangelog', { defaultValue: "What's New" })}
              </Link>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Update available prompt dialog
  if (needsRefresh) {
    return (
      <Dialog open={needsRefresh} onOpenChange={(open) => !open && dismissUpdate()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              {t('pwa.updateAvailable', { defaultValue: 'Update Available' })}
            </DialogTitle>
            <DialogDescription>
              {t('pwa.updateMessage', { defaultValue: 'A new version of ChaosKitchen is available with improvements and new features!' })}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4 py-4">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/10 border border-primary/20">
              <Download className="w-8 h-8 text-primary" />
              <div>
                <p className="font-medium text-foreground">
                  {t('pwa.newVersion', { defaultValue: 'New Version Ready' })}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('pwa.updateDesc', { defaultValue: 'Click update to get the latest features' })}
                </p>
              </div>
            </div>

            <Link 
              to="/changelog" 
              className="flex items-center gap-2 text-sm text-primary hover:underline"
              onClick={dismissUpdate}
            >
              <FileText className="w-4 h-4" />
              {t('pwa.viewChangelog', { defaultValue: "See what's new" })}
            </Link>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={dismissUpdate}
              className="flex-1"
              disabled={isUpdating}
            >
              {t('pwa.updateLater', { defaultValue: 'Later' })}
            </Button>
            <Button
              onClick={updateServiceWorker}
              className="flex-1"
              disabled={isUpdating}
            >
              <Download className="w-4 h-4 mr-2" />
              {t('pwa.updateNow', { defaultValue: 'Update Now' })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return null;
};
